import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, Table, Pagination, Spinner, Alert, Badge, Row, Col, Container, Form, Button } from 'react-bootstrap';
import 'bootstrap/dist/css/bootstrap.min.css';
import apiService from '../services/apiService';
import Breadcrumb from '../components/Breadcrumb';
import { FiRefreshCcw } from 'react-icons/fi';

// Get variant for status badges
const getBadgeVariant = (status) => {
  switch (status) {
    case 'COMPLETED':
      return 'success';
    case 'FAILED':
      return 'danger';
    case 'RUNNING':
      return 'primary';
    case 'PENDING':
      return 'warning';
    default:
      return 'secondary';
  }
};

// Function to format datetime with nanoseconds
const formatDateTimeWithNanos = (dateString) => {
    if (!dateString) return null;
    
    try {
        const date = new Date(dateString);
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');
        const seconds = String(date.getSeconds()).padStart(2, '0');
        const milliseconds = String(date.getMilliseconds()).padStart(3, '0');
        
        // Extract nanoseconds from the original string if available
        const nanoMatch = dateString.match(/\.(\d{3,9})/);
        let nanos = '000000';
        if (nanoMatch) {
            const fullNanos = nanoMatch[1].padEnd(9, '0');
            nanos = fullNanos.substring(3, 9); // Get microseconds + nanoseconds
        }
        
        return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
    } catch (error) {
        return dateString;
    }
};

// Get status icon
const getStatusIcon = (status) => {
  switch (status) {
    case 'COMPLETED':
      return '✓';
    case 'FAILED':
      return '✗';
    case 'RUNNING':
      return '⟳';
    case 'PENDING':
      return '⏳';
    default:
      return '●';
  }
};

// Function to format address from property data
const formatAddress = (propertyData) => {
    if (!propertyData || !Array.isArray(propertyData) || propertyData.length === 0) {
        return 'No address available';
    }
    
    const property = propertyData[0]; // Get first property
    const { address1, address2, city, state, zipcode } = property;
    
    let formattedAddress = '';
    if (address1) formattedAddress += address1;
    if (address2) formattedAddress += `, ${address2}`;
    if (city) formattedAddress += formattedAddress ? `, ${city}` : city;
    if (state) formattedAddress += `, ${state}`;
    if (zipcode) formattedAddress += ` ${zipcode}`;
    
    return formattedAddress || 'Address not available';
};

const AutomationRuns = () => {
    const navigate = useNavigate();
    const [runs, setRuns] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalRuns, setTotalRuns] = useState(0);
    const [showFilters, setShowFilters] = useState(false);
    const [selectedFilter, setSelectedFilter] = useState('all');
    const [showFailedFilter, setShowFailedFilter] = useState(false);
    const [failedFilterValue, setFailedFilterValue] = useState('');
    const pageSize = 50; // Define page size

    // Filter options for status filtering
    const filterOptions = [
        { value: 'all', label: 'All Closing Coordinator', description: 'All automation runs' },
        { value: 'completed', label: 'Completed', description: 'Successfully completed runs' },
        { value: 'failed', label: 'Failed', description: 'Failed automation runs' },
        { value: 'running', label: 'Running', description: 'Currently running automations' },
        { value: 'pending', label: 'Pending', description: 'Pending automation runs' }
    ];

    // Handle filter change
    const handleFilterChange = (filterValue) => {
        setSelectedFilter(filterValue);
        setCurrentPage(1); // Reset to first page when filter changes
        
        // Show/hide the failed filter based on selection
        if (filterValue === 'failed') {
            setShowFailedFilter(true);
        } else {
            setShowFailedFilter(false);
            setFailedFilterValue(''); // Reset failed filter value
        }
    };

    // Handle failed filter change
    const handleFailedFilterChange = (value) => {
        setFailedFilterValue(value);
        // Only call API on successful filter changes, not on failures
        if (value !== '') {
            setCurrentPage(1);
            fetchAutomationRuns(1);
        }
    };

    // Function to fetch automation runs with optional status filter
    const fetchAutomationRuns = useCallback(async (page) => {
        setLoading(true);
        setError(null);
        try {
            // Determine the status filter
            const statusFilter = selectedFilter !== 'all' ? selectedFilter : null;
            
            // Use filtered API for both failed filter and completion status filtering
            let data = await apiService.getAutomationRuns(page, pageSize, statusFilter,failedFilterValue);

            
            setRuns(data.results);
            setTotalRuns(data.count);
            setTotalPages(data.num_pages);
        } catch (err) {
            setError('Failed to fetch automation runs.');
            console.error('Error fetching automation runs:', err);
        } finally {
            setLoading(false);
        }
    }, [pageSize, selectedFilter, failedFilterValue]);

    // Handle real-time updates
    const handleRunUpdate = useCallback((updatedRun) => {
        setRuns(prevRuns => {
            // Check if the updated run already exists in our list
            const existingIndex = prevRuns.findIndex(run => run.id === updatedRun.id);
            
            if (existingIndex >= 0) {
                // Update existing run
                const updatedRuns = [...prevRuns];
                updatedRuns[existingIndex] = updatedRun;
                return updatedRuns;
            } else {
                // Add new run at the beginning (newest first)
                refreshCurrentPage()
                return [updatedRun, ...prevRuns.slice(0, pageSize - 1)];
            }

        });
    }, [pageSize]);

    // Refresh current page without resetting pagination
    const refreshCurrentPage = useCallback(() => {
        fetchAutomationRuns(currentPage);
    }, [fetchAutomationRuns, currentPage]);

    // Initial data fetch
    useEffect(() => {
        fetchAutomationRuns(currentPage);
    }, [currentPage, fetchAutomationRuns]);

    // Refetch data when filter changes
    useEffect(() => {
        fetchAutomationRuns(1);
        setCurrentPage(1);
    }, [selectedFilter, fetchAutomationRuns]);

    // Setup WebSocket connection for real-time updates
    useEffect(() => {
        const cleanupWebSocket = apiService.setupAutomationRunsWebSocket(handleRunUpdate);
        refreshCurrentPage()

        // Cleanup function
        return () => {
            if (typeof cleanupWebSocket === 'function') {
                cleanupWebSocket();
            }
        };
    }, []);

    // Mark all notifications as read when Closing Coordinator Trigger page is opened
    useEffect(() => {
        const markNotificationsAsRead = async () => {
            try {
                await apiService.markAllNotificationsAsRead();
                // Dispatch event to update notification count in other components
                window.dispatchEvent(new CustomEvent('notificationCountUpdate', { detail: { unread_count: 0 } }));
            } catch (error) {
                console.error('Error marking notifications as read:', error);
            }
        };

        markNotificationsAsRead();
    }, []);

    // Handle page change
    const handlePageChange = (pageNumber) => {
        setCurrentPage(pageNumber);
    };

    // Render professional status badge with enhanced styling
    const renderStatusBadge = (status, completion_status= null) => {
        const getStatusConfig = (status) => {
            switch (status) {
                case 'completed':
                    return {
                        bg: 'success',
                        icon: '✓',
                        gradient: 'linear-gradient(135deg, #28a745 0%, #20c997 100%)',
                        shadow: '0 4px 15px rgba(40, 167, 69, 0.3)',
                        pulse: false
                    };
                case 'failed':
                    return {
                        bg: 'danger',
                        icon: '✗',
                        gradient: 'linear-gradient(135deg, #dc3545 0%, #e74c3c 100%)',
                        shadow: '0 4px 15px rgba(220, 53, 69, 0.3)',
                        pulse: false
                    };
                case 'running':
                    return {
                        bg: 'primary',
                        icon: '⟳',
                        gradient: 'linear-gradient(135deg, #007bff 0%, #6610f2 100%)',
                        shadow: '0 4px 15px rgba(0, 123, 255, 0.3)',
                        pulse: true
                    };
                case 'pending':
                    return {
                        bg: 'warning',
                        icon: '⏳',
                        gradient: 'linear-gradient(135deg, #ffc107 0%, #fd7e14 100%)',
                        shadow: '0 4px 15px rgba(255, 193, 7, 0.3)',
                        pulse: true
                    };
                default:
                    return {
                        bg: 'secondary',
                        icon: '●',
                        gradient: 'linear-gradient(135deg, #6c757d 0%, #495057 100%)',
                        shadow: '0 4px 15px rgba(108, 117, 125, 0.3)',
                        pulse: false
                    };
            }
        };

        const config = getStatusConfig(status);

        const cs = (completion_status || '').toLowerCase();
        let displayText = status;
        if(completion_status !== null) {        
        if (status === 'failed' && cs === 'fsbo_no_agents') {
            displayText = 'FSBO';
        } else if (status === 'failed' &&  cs === 'agent_copy_missing') {
            displayText = 'Agent Copy Failed';
        } else if (status === 'failed' && cs === 'refinance') {
            displayText = 'Refinance';
        }else if (status === 'completed' && cs === 'fsbo_but_selling_agents') {
            displayText = 'FSBO, ONLY S/A';
        } else if (status === 'completed' && cs === 'fsbo_but_listing_agents') {
            displayText = 'FSBO, ONLY L/A';
        }
    }
        
        return (
            <div 
                className={`status-badge-professional ${config.pulse ? 'status-badge-pulse' : ''}`}
                style={{
                    background: config.gradient,
                    boxShadow: config.shadow,
                    color: 'white',
                    padding: '0.4rem 0.8rem',
                    borderRadius: '8px',
                    fontSize: '0.7rem',
                    fontWeight: '600',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '0.4rem',
                    border: 'none',
                    textTransform: 'uppercase',
                    letterSpacing: '0.3px',
                    transition: 'all 0.3s ease',
                    cursor: 'default',
                    minWidth: '85px',
                    justifyContent: 'center'
                }}
            >
                <span 
                    className={`status-icon ${config.pulse ? 'status-icon-spin' : ''}`}
                    style={{ 
                        fontSize: '0.9rem',
                        display: 'inline-block'
                    }}
                >
                    {config.icon}
                </span>
                <span>{displayText}</span>
            </div>
        );
    };

    // Handle row click to navigate to logs page
    const handleRowClick = (automationRunId) => {
        navigate(`/automation/logs/${automationRunId}`);
    };

    const breadcrumbItems = [
        { label: 'Agent Copies', href: '#' },
        { label: 'Closing Coordinator', href: null }
    ];

    return (
        <Container fluid className="px-4 py-4" style={{ backgroundColor: '#f8f9fa', minHeight: '100vh' }}>
            <Breadcrumb items={breadcrumbItems} />
            <style jsx>{`
                .page-title {
                    color: #2c3e50;
                    font-weight: 700;
                    font-size: 2rem;
                    text-shadow: 0 2px 4px rgba(0,0,0,0.1);
                }
                
                .page-subtitle {
                    font-size: 1rem;
                    color: #6c757d;
                }
                
                .stats-summary {
                    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
                    border-radius: 12px;
                    padding: 1rem 1.5rem;
                    color: white;
                    margin-bottom: 1.5rem;
                    box-shadow: 0 4px 15px rgba(102, 126, 234, 0.3);
                }
                
                .automation-runs-container {
                    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
                    border-radius: 20px;
                    padding: 2rem;
                    margin-bottom: 2rem;
                    color: white;
                    box-shadow: 0 10px 30px rgba(0,0,0,0.1);
                }
                
                .stats-card {
                    background: rgba(255,255,255,0.1);
                    backdrop-filter: blur(10px);
                    border: 1px solid rgba(255,255,255,0.2);
                    border-radius: 15px;
                    padding: 1.5rem;
                    color: white;
                    transition: transform 0.3s ease;
                }
                
                .stats-card:hover {
                    transform: translateY(-5px);
                }
                
                .main-card {
                    border: none;
                    border-radius: 20px;
                    box-shadow: 0 10px 40px rgba(0,0,0,0.1);
                    overflow: hidden;
                }
                
                .card-header-custom {
                    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
                    color: white;
                    border: none;
                    padding: 1.5rem 2rem;
                }
                
                .table-modern {
                    width: 100%;
                    margin-bottom: 0;
                    table-layout: auto;
                }
                
                .table-responsive {
                    overflow-x: auto !important;
                    overflow-y: visible;
                }
                
                .table-modern thead th {
                    background-color: #f8f9fa;
                    border: none;
                    font-weight: 600;
                    color: #495057;
                    padding: 12px 15px;
                    font-size: 0.85rem;
                    text-transform: uppercase;
                    letter-spacing: 0.3px;
                    white-space: nowrap;
                    border-bottom: 2px solid #e9ecef;
                }
                
                .table-modern thead th:nth-child(1) { min-width: 100px; }  /* ID */
                .table-modern thead th:nth-child(2) { min-width: 140px; } /* Name */
                .table-modern thead th:nth-child(3) { min-width: 250px; } /* Address */
                .table-modern thead th:nth-child(4) { min-width: 220px; } /* Status */
                .table-modern thead th:nth-child(5) { min-width: 120px; } /* Started */
                .table-modern thead th:nth-child(6) { min-width: 120px; } /* Completed */
                .table-modern thead th:nth-child(7) { min-width: 100px; } /* Duration */
                .table-modern thead th:nth-child(8) { min-width: 450px; } /* Error */
                
                .table-modern tbody rd-wrap: break-word;
                    white-space: nowrap;
                    overflow: hidden;
                    text-overflow: ellipsis;
                }
                
                .table-modern {
                    min-width: 1500px;
                    table-layout: fixed;
                    width: 100%;
                }
                
                .loading-container {
                    padding: 4rem 2rem;
                    text-align: center;
                }
                
                .loading-spinner {
                    width: 3rem;
                    height: 3rem;
                    border-width: 0.3rem;
                }
                
                .empty-state {
                    padding: 4rem 2rem;
                    text-align: center;
                    color: #6c757d;
                }
                
                .empty-state-icon {
                    font-size: 4rem;
           0        margin-bottom: 1rem;
                    opacity: 0.5;
                }
                
                .pagination-container {
                    background-color: #f8f9fa;
                    padding: 1.5rem 2rem;
                    border-top: 1px solid #e9ecef;
                }
                
                .pagination-info {
                    color: #6c757d;
                    font-weight: 500;
                }
                
                /* Enhanced Filter Animations */
                @keyframes slideDown {
                    from {
                        opacity: 0;
                        transform: translateY(-20px);
                    }
                    to {
                        opacity: 1;
                        transform: translateY(0);
                    }
                }
                
                @keyframes pulse {
                    0% {
                        box-shadow: 0 4px 12px rgba(23, 162, 184, 0.3);
                    }
                    50% {
                        box-shadow: 0 6px 20px rgba(23, 162, 184, 0.5);
                    }
                    100% {
                        box-shadow: 0 4px 12px rgba(23, 162, 184, 0.3);
                    }
                }
                
                @keyframes fadeInUp {
                    from {
                        opacity: 0;
                        transform: translateY(10px);
                    }
                    to {
                        opacity: 1;
                        transform: translateY(0);
                    }
                }
                
                .filter-card {
                    animation: slideDown 0.3s ease-out;
                }
                
                .filter-controls {
                    animation: fadeInUp 0.4s ease-out 0.1s both;
                }
                
                .filter-info {
                    animation: fadeInUp 0.4s ease-out 0.2s both;
                }
                
                .filter-stats {
                    animation: fadeInUp 0.4s ease-out 0.3s both;
                }
                
                .stat-item {
                    transition: all 0.3s ease;
                    padding: 0.5rem;
                    border-radius: 8px;
                }
                
                .stat-item:hover {
                    background-color: rgba(0,0,0,0.05);
                    transform: translateY(-2px);
                }
                
                .form-select-modern:hover {
                    border-color: #667eea !important;
                    transform: translateY(-1px);
                }
                
                .info-badge {
                    transition: all 0.3s ease;
                }
                
                .info-badge:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 6px 20px rgba(23, 162, 184, 0.4) !important;
                }
                
                .btn-group .btn {
                    transition: all 0.3s ease;
                }
                
                .btn-group .btn:hover {
                    transform: translateY(-1px);
                    box-shadow: 0 4px 12px rgba(0,0,0,0.15);
                }
                
                .duration-badge {
                    background-color: #e9ecef;
                    color: #495057;
                    padding: 0.25rem 0.75rem;
                    border-radius: 20px;
                    font-size: 0.75rem;
                    font-weight: 500;
                }
                
                .error-tooltip {
                    max-width: 400px;
                    overflow: hidden;
                    text-overflow: ellipsis;
                    white-space: nowrap;
                    background-color: #fff5f5;
                    color: #c53030;
                    padding: 0.5rem 0.75rem;
                    border-radius: 8px;
                    border-left: 3px solid #fc8181;
                    font-size: 0.75rem;
                    line-height: 1.4;
                    cursor: help;
                    transition: all 0.2s ease;
                }
                
                .error-tooltip:hover {
                    background-color: #fed7d7;
                    transform: translateY(-1px);
                    box-shadow: 0 2px 8px rgba(197, 48, 48, 0.15);
                }
                
                
                
                .name-cell {
                    font-weight: 600;
                    color: #2d3748;
                }
                
                .date-cell {
                    color: #4a5568;
                    font-size: 0.75rem;
                    font-family: 'Monaco', 'Menlo', 'Consolas', monospace;
                    white-space: nowrap;
                    overflow: hidden;
                    text-overflow: ellipsis;
                    max-width: 180px;
                }
                
                .address-cell {
                    color: #4a5568;
                    font-size: 0.75rem;
                    line-height: 1.3;
                    max-width: 200px;
                    overflow: hidden;
                    text-overflow: ellipsis;
                    white-space: nowrap;
                    padding: 0.5rem 0.5rem;
                }
                
                .address-cell:hover {
                    white-space: normal;
                    word-wrap: break-word;
                    overflow: visible;
                    background-color: #f8f9fa;
                    border-radius: 4px;
                    box-shadow: 0 2px 8px rgba(0,0,0,0.1);
                    z-index: 10;
                    position: relative;
                }
                
                /* Professional Status Badge Styles */
                .status-badge-professional {
                    display: inline-flex;
                    align-items: center;
                    padding: 3px 6px;
                    border-radius: 4px;
                    font-size: 0.7rem;
                    font-weight: 600;
                    gap: 3px;
                    letter-spacing: 0.2px;
                    min-width: 70px;
                    justify-content: center;
                    transition: all 0.3s ease;
                    position: relative;
                    overflow: hidden;
                    text-transform: uppercase;
                    border: 1px solid rgba(255, 255, 255, 0.2);
                    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.1);
                }
                
                .status-badge-professional::before {
                    content: '';
                    position: absolute;
                    top: 0;
                    left: -100%;
                    width: 100%;
                    height: 100%;
                    background: linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent);
                    transition: left 0.5s ease;
                }
                
                .status-badge-professional:hover::before {
                    left: 100%;
                }
                
                .status-badge-professional:hover {
                    transform: translateY(-2px) scale(1.05);
                    filter: brightness(1.1);
                }
                
                .status-badge-pulse {
                    animation: statusPulse 2s infinite;
                }
                
                @keyframes statusPulse {
                    0% {
                        box-shadow: 0 4px 15px rgba(0, 123, 255, 0.3);
                        transform: scale(1);
                    }
                    50% {
                        box-shadow: 0 6px 25px rgba(0, 123, 255, 0.5);
                        transform: scale(1.02);
                    }
                    100% {
                        box-shadow: 0 4px 15px rgba(0, 123, 255, 0.3);
                        transform: scale(1);
                    }
                }
                
                .status-icon-spin {
                    animation: iconSpin 2s linear infinite;
                }
                
                @keyframes iconSpin {
                    from {
                        transform: rotate(0deg);
                    }
                    to {
                        transform: rotate(360deg);
                    }
                }
                
                /* Status Badge Variants */
                .status-badge-professional.status-completed {
                    background: linear-gradient(135deg, #28a745 0%, #20c997 100%) !important;
                }
                
                .status-badge-professional.status-failed {
                    background: linear-gradient(135deg, #dc3545 0%, #e74c3c 100%) !important;
                }
                
                .status-badge-professional.status-running {
                    background: linear-gradient(135deg, #007bff 0%, #6610f2 100%) !important;
                }
                
                .status-badge-professional.status-pending {
                    background: linear-gradient(135deg, #ffc107 0%, #fd7e14 100%) !important;
                }
                
                /* Enhanced table row hover effect for status badges */
                .table-modern tbody tr:hover .status-badge-professional {
                    transform: translateY(-1px) scale(1.03);
                    filter: brightness(1.15);
                }
                
                /* Status column specific styling */
                .status-cell {
                    padding: 10px 20px !important;
                    // text-align: center;
                    white-space: nowrap;
                }
                
                /* Error column specific styling */
                .error-cell {
                    min-width: 450px !important;
                    max-width: 450px !important;
                    padding: 10px 15px !important;
                    overflow: hidden;
                    text-overflow: ellipsis;
                    white-space: nowrap;
                }
                
                .error-cell:hover {
                    white-space: normal;
                    word-wrap: break-word;
                    overflow: visible;
                    background-color: #f8f9fa;
                    border-radius: 4px;
                    box-shadow: 0 2px 8px rgba(0,0,0,0.1);
                    z-index: 10;
                    position: relative;
                    max-height: 200px;
                    overflow-y: auto;
                }
            `}</style>
            
            {/* Page Header Section */}
            <Row className="mb-4">
                <Col>
                    <div className="d-flex justify-content-between align-items-center">
                        <div>
                            <h2 className="page-title mb-1">Closing Coordinator</h2>
                            <p className="page-subtitle text-muted mb-0">
                                Monitor and track your Closing Coordinator automation executions in real-time
                            </p>
                        </div>
                        <div className="d-flex align-items-center gap-3">
                            
                            <div className="btn-group" role="group">
                                 <Button 
          variant={showFilters ? "primary" : "outline-primary"}
          onClick={() => setShowFilters(!showFilters)}
          className="d-flex align-items-center gap-2"
        >
          {showFilters ? 'Filters' : 'Filters'}
                    <i className="fas fa-filter"></i>

        </Button>
                            </div>
                        </div>
                    </div>
                </Col>
            </Row>

            {/* Enhanced Professional Filter Section */}
            {showFilters && (
                <Card 
                    className="mb-4 filter-card" 
                    style={{ 
                        border: 'none', 
                        boxShadow: '0 8px 25px rgba(0,0,0,0.12)',
                        borderRadius: '16px',
                        background: 'linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%)',
                        animation: 'slideDown 0.3s ease-out'
                    }}
                >
                    <Card.Header 
                        style={{
                            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                            color: 'white',
                            border: 'none',
                            borderRadius: '16px 16px 0 0',
                            padding: '1rem 1.5rem'
                        }}
                    >
                        <div className="d-flex align-items-center justify-content-between">
                            <div className="d-flex align-items-center">
                                <i className="fas fa-sliders-h me-2"></i>
                                <h6 className="mb-0 fw-bold">Filters</h6>
                            </div>
                            <div className="d-flex align-items-center gap-2">
                                <button 
                                    className="btn btn-sm btn-outline-light"
                                    onClick={() => setShowFilters(false)}
                                    style={{ borderRadius: '6px', padding: '0.25rem 0.5rem' }}
                                >
                                    <i className="fas fa-times"></i>
                                </button>
                            </div>
                        </div>
                    </Card.Header>
                    <Card.Body style={{ padding: '1rem 1rem 2rem 1rem !important' }}>
                        <Row className="align-items-center g-4 mb-2">
                            <Col lg={3}>
                                <div className="filter-controls">
                                    <label className="form-label fw-semibold text-muted mb-2">
                                        Status Filter
                                    </label>
                                    <div className="position-relative">
                                        <Form.Select
                                            value={selectedFilter}
                                            onChange={(e) => handleFilterChange(e.target.value)}
                                            className="form-select-modern"
                                            style={{
                                                border: '2px solid #e9ecef',
                                                borderRadius: '12px',
                                                fontSize: '0.95rem',
                                                padding: '0.75rem 1rem',
                                                background: 'white',
                                                boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
                                                transition: 'all 0.3s ease',
                                                cursor: 'pointer'
                                            }}
                                            onFocus={(e) => {
                                                e.target.style.borderColor = '#667eea';
                                                e.target.style.boxShadow = '0 0 0 0.2rem rgba(102, 126, 234, 0.25)';
                                            }}
                                            onBlur={(e) => {
                                                e.target.style.borderColor = '#e9ecef';
                                                e.target.style.boxShadow = '0 2px 8px rgba(0,0,0,0.05)';
                                            }}
                                        >
                                            {filterOptions.map((option) => (
                                                <option key={option.value} value={option.value}>
                                                    {option.label}
                                                </option>
                                            ))}
                                        </Form.Select>
                                        <i className="fas fa-chevron-down position-absolute" 
                                           style={{
                                               right: '1rem',
                                               top: '50%',
                                               transform: 'translateY(-50%)',
                                               pointerEvents: 'none',
                                               color: '#6c757d',
                                               fontSize: '0.8rem'
                                           }}>
                                        </i>
                                    </div>
                                </div>
                            </Col>
                            
                            {showFailedFilter && (
                                <Col lg={3}>
                                    <div className="filter-controls">
                                        <label className="form-label fw-semibold text-muted mb-2">
                                            Failed Automation
                                        </label>
                                        <div className="position-relative">
                                            <Form.Select
                                                value={failedFilterValue}
                                                onChange={(e) => handleFailedFilterChange(e.target.value)}
                                                className="form-select-modern"
                                                style={{
                                                    border: '2px solid #e9ecef',
                                                    borderRadius: '12px',
                                                    fontSize: '0.95rem',
                                                    padding: '0.75rem 1rem',
                                                    background: 'white',
                                                    boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
                                                    transition: 'all 0.3s ease',
                                                    cursor: 'pointer'
                                                }}
                                            >
                                                <option value="">Select Failed Automation</option>
                                                <option value="listing_agents_only">Listing Agents Only</option>
                                                <option value="selling_agents_only">Selling Agents Only</option>
                                                <option value="fsbo_no_agents">FSBO - No Agents</option>
                                                <option value="agent_copy_missing">Agent Copy Missing</option>
                                                <option value="both_agents_missing">Both Agents Missing</option>
                                                <option value="no_completion_status">Failed Unexpected</option>
                                            </Form.Select>
                                        </div>
                                    </div>
                                </Col>
                            )}
                            
                        </Row>
                        
                        {/* Filter Statistics */}
                        <Row className="mt-0 pt-3 pb-2" style={{ borderTop: '1px solid #e9ecef', minHeight: '60px' }}>
                            <Col>
                                <div className="d-flex align-items-center justify-content-end flex-wrap gap-3 h-100">
                                    
                                    <div className="filter-actions d-flex align-items-center justify-content-center" style={{ height: '100%' }}>
                                        <button 
                                            className="btn btn-outline-primary btn-sm me-2"
                                            onClick={() => {
                                                setSelectedFilter('all');
                                                setCurrentPage(1);
                                            }}
                                            style={{ 
                                                borderRadius: '8px',
                                                height: '36px',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center'
                                            }}
                                        >
                                            <i className="fas fa-refresh me-1"></i>
                                            Reset
                                        </button>
                                        <button 
                                            className="btn btn-primary btn-sm"
                                            onClick={() => fetchAutomationRuns(1)}
                                            style={{ 
                                                borderRadius: '8px',
                                                height: '36px',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center'
                                            }}
                                        >
                                            <i className="fas fa-sync-alt me-1"></i>
                                            Refresh
                                        </button>
                                    </div>
                                </div>
                            </Col>
                        </Row>
                    </Card.Body>
                </Card>
            )}

          

            {/* Statistics Summary Cards */}
            <Row className="mb-4">
                <Col md={3}>
                    <Card className="border-0 shadow-sm h-100" style={{
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        color: 'white'
                    }}>
                        <Card.Body className="d-flex align-items-center">
                            <div className="flex-grow-1">
                                <div className="text-white-50 small text-uppercase fw-bold mb-1">
                                    Total Records
                                </div>
                                <div className="h4 mb-0 fw-bold">
                                    {totalRuns}
                                </div>
                            </div>
                            <div className="ms-3">
                                <i className="fas fa-play-circle fa-2x opacity-75"></i>
                            </div>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            {/* Main Content Card */}
            <Card className="main-card">
                <Card.Header className="card-header-custom">
                    <Row className="align-items-center">
                        <Col>
                            <h5 className="mb-0 fw-bold">{filterOptions.find(opt => opt.value === selectedFilter)?.label}</h5>
                        </Col>
                        
                    </Row>
                </Card.Header>
                
                <Card.Body className="p-0">
                    {loading && (
                        <div className="loading-container">
                            <Spinner 
                                animation="border" 
                                variant="primary" 
                                className="loading-spinner mb-3"
                            />
                            <h5 className="text-muted">Loading automation runs...</h5>
                            <p className="text-muted mb-0">Please wait while we fetch the latest data</p>
                        </div>
                    )}
                    
                    {error && (
                        <div className="p-4">
                            <Alert variant="danger" className="border-0 rounded-3">
                                <Alert.Heading className="h6">⚠️ Error Loading Data</Alert.Heading>
                                {error}
                            </Alert>
                        </div>
                    )}
                    
                    {!loading && !error && (
                        <>
                            {runs.length > 0 ? (
                                <div className="table-responsive">
                                    <Table hover className="table-modern mb-0">
                                        <thead>
                                            <tr>
                                                <th>ID</th>
                                                <th>Name</th>
                                                <th>Address</th>
                                                <th>Qualia Status</th>
                                                <th>Automation Status</th>

                                                <th>Started</th>
                                                <th>Completed</th>
                                                <th>Duration</th>
                                                <th>Error</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {runs.map((run, index) => (
                                                <tr 
                                                    key={run.id} 
                                                    onClick={() => handleRowClick(run.id)}
                                                    style={{cursor: 'pointer'}}
                                                >
                                                    <td>
                                                        <span className="id-badge"># {runs.length - index}</span>
                                                    </td>
                                                    <td className="name-cell" title={run.name}>
                                                        {run?.name?.length > 12 ? `${run.name.substring(0, 12)}...` : run.name}
                                                    </td>
                                                    <td className="address-cell" title={formatAddress(run.property_data)}>
                                                        {formatAddress(run?.properties)}
                                                    </td>
                                                    <td className="status-cesll">{renderStatusBadge(run.qualia_status || "CLOSED")}</td>
                                                    <td className="status-cell" style={{minWidth: '13rem'}}>{renderStatusBadge(run.status, run.completion_status)} {run?.rerun ? (
                                                        <span
                                                            className="rerun-badge"
                                                            title="Rerun"
                                                            style={{
                                                                 // marginLeft: '8px',
                                                                padding: '2px 4px',
                                                                borderRadius: '10px',
                                                                fontSize: '0.70rem',
                                                                fontWeight: 600,
                                                                textTransform: 'uppercase',
                                                                letterSpacing: '0.4px',
                                                                backgroundColor: '#e8f0ff',
                                                                color: '#1a3d7c',
                                                                border: '1px solid #c9dbff'
                                                            }}
                                                        ><FiRefreshCcw style={{ verticalAlign: 'middle' }} /></span>
                                                    ) : null} </td>
                                                    <td className="date-cell" title={formatDateTimeWithNanos(run.started_at)}>
                                                        <div style={{ fontSize: '0.75rem', lineHeight: '1.2' }}>
                                                            {formatDateTimeWithNanos(run.started_at)}
                                                        </div>
                                                    </td>
                                                    <td className="date-cell" title={run.completed_at ? formatDateTimeWithNanos(run.completed_at) : 'In Progress'}>
                                                        {run.completed_at ? (
                                                            <div style={{ fontSize: '0.75rem', lineHeight: '1.2' }}>
                                                                {formatDateTimeWithNanos(run.completed_at)}
                                                            </div>
                                                        ) : (
                                                            <span className="text-muted">Pending</span>
                                                        )}
                                                    </td>
                                                    <td>
                                                        {run.duration ? (
                                                            <span className="duration-badge">
                                                                {run.duration.toFixed(1)}s
                                                            </span>
                                                        ) : (
                                                            <span className="text-muted">—</span>
                                                        )}
                                                    </td>
                                                    <td className="error-cell">
                                                        {run.error ? (
                                                            <div className="error-tooltip" style={{cursor:"pointer"}} title={run.error}>
                                                                {run.error.length > 50 ? `${run.error.substring(0, 50)}...` : run.error}
                                                            </div>
                                                        ) : (
                                                            <span className="text-success" style={{ fontSize: '0.8rem' }}>✓ OK</span>
                                                        )}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </Table>
                                </div>
                            ) : (
                                <div className="empty-state">
                                    <div className="empty-state-icon">🔍</div>
                                    <h5 className="mb-2">
                                        {selectedFilter === 'all' ? 'No Automation Runs Found' : `No ${filterOptions.find(opt => opt.value === selectedFilter)?.label} Found`}
                                    </h5>
                                    <p className="mb-0">
                                        {selectedFilter === 'all' 
                                            ? 'Start your first automation to see results here'
                                            : `No automation runs match the current filter criteria. Try adjusting your filters or check back later.`
                                        }
                                    </p>
                                    {selectedFilter !== 'all' && (
                                        <Button 
                                            variant="outline-primary" 
                                            size="sm" 
                                            className="mt-3"
                                            onClick={() => handleFilterChange('all')}
                                        >
                                            Show All Runs
                                        </Button>
                                    )}
                                </div>
                            )}
                            
                            {runs.length > 0 && (
                                <div className="pagination-container">
                                    <Row className="align-items-center">
                                        <Col>
                                            <div className="pagination-info">
                                                {/* Showing <strong>{runs.length}</strong> of <strong>{totalRuns}</strong> entries */}
                                            </div>
                                        </Col>
                                        <Col xs="auto">
                                            <Pagination className="mb-0">
                                                <Pagination.First
                                                    onClick={() => handlePageChange(1)}
                                                    disabled={currentPage === 1}
                                                />
                                                <Pagination.Prev 
                                                    onClick={() => handlePageChange(currentPage - 1)} 
                                                    disabled={currentPage === 1}
                                                />
                                                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                                                    // Show 5 pages around current page
                                                    let pageNum;
                                                    if (totalPages <= 5) {
                                                        pageNum = i + 1;
                                                    } else if (currentPage <= 3) {
                                                        pageNum = i + 1;
                                                    } else if (currentPage >= totalPages - 2) {
                                                        pageNum = totalPages - 4 + i;
                                                    } else {
                                                        pageNum = currentPage - 2 + i;
                                                    }
                                                    
                                                    return (
                                                        <Pagination.Item 
                                                            key={pageNum} 
                                                            active={pageNum === currentPage}
                                                            onClick={() => handlePageChange(pageNum)}
                                                        >
                                                            {pageNum}
                                                        </Pagination.Item>
                                                    );
                                                })}
                                                <Pagination.Next 
                                                    onClick={() => handlePageChange(currentPage + 1)} 
                                                    disabled={currentPage === totalPages}
                                                />
                                                <Pagination.Last 
                                                    onClick={() => handlePageChange(totalPages)}
                                                    disabled={currentPage === totalPages}
                                                />
                                            </Pagination>
                                        </Col>
                                    </Row>
                                </div>
                            )}
                        </>
                    )}
                </Card.Body>
            </Card>
        </Container>
    );
};

export default AutomationRuns;